import { useCallback, useRef, useState } from 'react';

import sleep from '@zr/utilities/sleep';

import {
  fixMergedResume,
  SUCCESS,
} from '@zr/utilities/api/mergedResume';

import {
  EXPERIENCE,
  EDUCATION,
} from '@zr/utilities/profileFields';

import {
  experienceFormikValidateOnlySubmit,
  transformExperienceFormToPatchEntry,
  transformExperienceServerEntryToFormData,
  validationSchema as experienceValidationSchema,
} from '@zr/jobseeker-public/apps/profile-form/utilities/experienceFormUtilities';

import {
  educationFormikValidateOnlySubmit,
  transformEducationFormToPatchEntry,
  transformEducationServerEntryToFormData,
  validationSchema as educationValidationSchema,
} from '@zr/jobseeker-public/apps/profile-form/utilities/educationFormUtilities';

import IncompleteExperienceFieldset from '@zr/jobseeker-public/apps/profile-form/components/Formik/IncompleteExperienceFieldset';
import IncompleteEducationFieldset from '@zr/jobseeker-public/apps/profile-form/components/Formik/IncompleteEducationFieldset';

/**
 * Server data looks like:

  incomplete_entries: {
   [EXPERIENCE]: [...experienceEntries],
   [EDUCATION]: [...educationEntries],
  }

 * And each entry looks like

  {
    section: { ...entryData }
    _idx: an integer
    missing: array of string fields that are 'missing' for the entry
  }

 * Merge them into one array, with each entry transformed into form friendly objects, so it's easier for the wizard to work with
 * @param {object} serverData
 * @returns {object} transformedData
 */
function transformServerDataToWizardFormData(serverData) {
  const serverEntries = serverData.incomplete_entries;
  // experience goes before education
  const transformedEntries = serverEntries[EXPERIENCE]?.reduce((allEntries, { _idx, missing, section }) => (
    [...allEntries, {
      entryKey: EXPERIENCE,
      entryInitialValues: {
        ...transformExperienceServerEntryToFormData(section),
        // see MergeParsedResume.jsx to see why this is needed
        fieldsetComponentKey: EXPERIENCE,
      },
      _idx,
      missing,
    }]
  ), []) ?? [];
  return serverEntries[EDUCATION]?.reduce((allEntries, { _idx, missing, section }) => (
    [...allEntries, {
      entryKey: EDUCATION,
      entryInitialValues: {
        ...transformEducationServerEntryToFormData(section),
        // see MergeParsedResume.jsx to see why this is needed
        fieldsetComponentKey: EDUCATION,
      },
      _idx,
      missing,
    }]
  ), transformedEntries) ?? transformedEntries;
}

/**
 * Custom hook that manages profile entry correction wizard state when initialized
 * to initialize: initializeWizard should be called with the 'needs_fix' response object from mergedResume.js
 * While the wizard is active, it returns all necessary data to display the current incomplete entry form to the React component
 *
 * @param {number} options.fadeTime buffer fade time in ms before advancing to the next entry, default to null for no fade time
 * @param {function} options.onError called when an unexpected error occurs, we should let the user retry if this happens
 * @param {function} options.onInitializedWizard called when successfully initialized wizard data for display
 * @param {function} options.onMergeFailure called when we unexpectedly ran into merging failures, we should abort out of this wizard
 * @param {function} options.onMergeSuccess called when the wizard is successfully done
 * @param {function} options.onStartSubmit called right before validating a submitted entry
 * @param {function} options.profileMethod profile_method string for analytics
 * @returns {object} wizardObject there are many exported properties, see below and MergeParsedResume.jsx
 */
export function useProfileEntryCorrectionWizard({
  fadeTime = null,
  onEntryInteraction = () => {},
  onEntryView = () => {},
  onError,
  onInitializedWizard = () => {},
  onMergeFailure,
  onMergeSuccess,
  onStartSubmit,
  // this should be overridden
  profileMethod = 'fe.jobseeker-parse-resume.useProfileEntryCorrectionWizard.validate_only',
}) {
  // wizard logic iterates through this state array
  const [incompleteEntries, setIncompleteEntries] = useState([]);
  // fade boolean that toggles fade animation
  const [isFading, setIsFading] = useState(false);
  // wizard current index in incompleteEntries
  const [entryIndex, setEntryIndex] = useState(0);
  // ensure we do not initialize a 2nd time
  const initializedRef = useRef(false);
  // user will use this wizard hook to build the patch data object and submit it to our servers when they finish all entries
  const patchDataRef = useRef(null);

  // wizard current entry object
  const currentEntry = incompleteEntries.length ? incompleteEntries[entryIndex] : null;
  // wizard current entry '_idx', used to identify specific entries
  // eslint-disable-next-line no-underscore-dangle
  const currentEntryProfileIdx = currentEntry?._idx ?? null;
  // 'missing' field joined as a string for analytics
  const currentEntryMissingString = currentEntry?.missing ? currentEntry.missing.join(' ') : '';
  // wizard current index + 1 for display progress
  const entryDisplayIndex = entryIndex + 1;
  // formik initialValues, this changes after each entry to prefill the next incomplete entry
  const entryInitialValues = currentEntry?.entryInitialValues;
  // wizard current entry key to identify experience or education
  const entryKey = currentEntry?.entryKey;
  // wizard total number of incomplete entries
  const totalEntries = incompleteEntries.length;
  // specific logic ran when finishing the last entry
  const isLastEntry = entryIndex >= (totalEntries - 1);
  // signaler to React component when initializeWizard is done and wizard can be shown
  const isWizardInitialized = totalEntries > 0;

  // entry data object for telemetry
  const entryTelemetryObject = {
    parse_entry_index: entryDisplayIndex,
    parse_entry_missing: currentEntryMissingString,
    parse_entry_type: entryKey,
    parse_total_entries: totalEntries,
  };

  const fieldsetComponentMap = {
    [EXPERIENCE]: IncompleteExperienceFieldset,
    [EDUCATION]: IncompleteEducationFieldset,
  };

  // returns which FieldsetComponent to use for the current entry
  const getFieldsetComponentByEntryKey = (key) => (
    fieldsetComponentMap[key] ?? null
  );

  // this is also directly exported, but unused due to reasons detailed in MergeParsedResume.jsx
  const FieldsetComponent = getFieldsetComponentByEntryKey(entryKey);

  const validationSchemaMap = {
    [EXPERIENCE]: experienceValidationSchema,
    [EDUCATION]: educationValidationSchema,
  };

  // which validationSchema to use for the current entry
  const entryValidationSchema = validationSchemaMap[entryKey] ?? null;

  // each current form field key mapped to true, we want formik fields to always be touched to display errors initially
  // errors shown only when touched is not formik specific, we code that logic in LabeledField
  const entryInitialTouched = entryInitialValues ? Object.keys(entryInitialValues).reduce((previous, fieldKey) => ({
    ...previous,
    [fieldKey]: true,
  }), {}) : {};

  /**
   * These handlers are just telemetry
   */
  const runEntryShowTelemetry = () => {
    onEntryView({
      ...entryTelemetryObject,
      viewName: 'entry_correction',
    });
  };

  const runEntrySkipTelemetry = () => {
    onEntryInteraction({
      ...entryTelemetryObject,
      interaction: 'skip',
    });
  };

  const runEntrySaveTelemetry = () => {
    onEntryInteraction({
      ...entryTelemetryObject,
      interaction: 'save',
    });
  };

  /**
   * Ran when user finishes the last entry
   * Runs the callback functions depending on the result
   *
   * @param {object} patchData the final patch data the user is submitting to fixMergedResume
   */
  const completeWizard = async (patchData) => {
    try {
      const response = await fixMergedResume(patchData);

      const { merge_resume_status: mergeResumeStatus } = response;

      if (mergeResumeStatus === SUCCESS) {
        await onMergeSuccess();
      } else {
        // this is bad news bear, because there's probably no way the jobseeker can fix this with the current UI
        await onMergeFailure();
      }
    } catch (e) {
      // as opposed to the fatal error one, we don't know why this failed (i.e. network issue), let them try again
      onError(e);
    }
  };

  /**
   * Exported function to initialize the wizard with the data from 'needs_fix' response
   */
  const initializeWizard = useCallback((serverData) => {
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;
    const wizardData = transformServerDataToWizardFormData(serverData);
    if (!wizardData.length) {
      // this shouldn't happen, but if it does i guess try to finish
      onMergeFailure();
    } else {
      patchDataRef.current = {
        ...serverData.incomplete_entries,
        profile_method: profileMethod,
      };
      setIncompleteEntries(wizardData);
      onInitializedWizard();
    }
  }, [onInitializedWizard, onMergeFailure, profileMethod]);

  /**
   * Ran when finishing the current entry
   * It will update patchDataRef and handle fade logic
   *
   * @param {object} patchData the next patch data to set after completing the current entry
   */
  const completeEntry = async (patchData) => {
    patchDataRef.current = patchData;
    if (fadeTime) {
      setIsFading(true);
      await sleep(fadeTime);
    }
    setEntryIndex((prevEntryIndex) => (prevEntryIndex + 1));
    if (fadeTime) {
      setIsFading(false);
    }
    runEntryShowTelemetry();
  };

  /**
   * Ran when we advance to the next entry and we have the next version of the patch data
   * Also handles last entry logic
   *
   * @param {object} nextPatchData  the next patch data to set after completing the current entry
   */
  const advance = async (nextPatchData = patchDataRef.current) => {
    if (isLastEntry) {
      await completeWizard(nextPatchData);
    } else {
      await completeEntry(nextPatchData);
    }
  };

  /**
   * Exported function that should be run when user skips an entry
   * Removes the skipped entry from patch data
   */
  const skipEntry = async () => {
    const nextEntrySection = patchDataRef.current[entryKey].filter(({ _idx }) => (
      _idx !== currentEntryProfileIdx
    ));
    const nextPatchData = {
      ...patchDataRef.current,
      [entryKey]: nextEntrySection,
    };
    runEntrySkipTelemetry();
    await advance(nextPatchData);
  };

  /**
   * Helper function that edits the specific section in patch data with the function parameter
   *
   * @param {object} section new entry that is patch ready
   * @returns {object} nextPatchData a copy of the current patch data with the updated entry
   */
  const getPatchDataWithNewSectionData = (section) => {
    const nextEntrySection = [...patchDataRef.current[entryKey]];
    const patchEntryIndex = nextEntrySection.findIndex(({ _idx }) => (
      _idx === currentEntryProfileIdx
    ));
    nextEntrySection[patchEntryIndex] = {
      section,
      _idx: currentEntryProfileIdx,
    };
    return {
      ...patchDataRef.current,
      [entryKey]: nextEntrySection,
    };
  };

  /**
   * Ran when the profile api successfully validates the entry form data
   * Transforms the form data into patch data and advances to the next entry
   *
   * @param {object} validatedFormData form data after passing validate_only profile api patch call
   */
  const onSubmitEntrySuccess = async (validatedFormData) => {
    let nextPatchData;
    switch (entryKey) {
      case EXPERIENCE: {
        const newSectionData = transformExperienceFormToPatchEntry(validatedFormData);
        nextPatchData = getPatchDataWithNewSectionData(newSectionData);
        break;
      }
      case EDUCATION: {
        const newSectionData = transformEducationFormToPatchEntry(validatedFormData);
        nextPatchData = getPatchDataWithNewSectionData(newSectionData);
        break;
      }
      default:
        nextPatchData = undefined;
    }
    runEntrySaveTelemetry();
    await advance(nextPatchData);
  };

  /**
   * Exported function that should be run when user fixes and submits an entry
   * Validates the form values with a validate_only profile api call and advances wizard if successful
   *
   * @param {object} formData submitted form entry values
   * @param {object} formikProps formik props from the formik library
   */
  const submitEntry = async (formData, formikProps) => {
    const validateOnlyParameters = {
      formData,
      formikProps,
      method: profileMethod,
      onError,
      onStartSubmit,
      onSuccess: async () => {
        await onSubmitEntrySuccess(formData);
      },
    };

    switch (entryKey) {
      case EXPERIENCE: {
        await experienceFormikValidateOnlySubmit(
          validateOnlyParameters,
        );
        break;
      }
      case EDUCATION: {
        await educationFormikValidateOnlySubmit(
          validateOnlyParameters,
        );
        break;
      }
      default:
        await advance();
    }
  };

  // parse entry correction intro is a simple step, just continue when they click the button
  const [introDone, setIntroDone] = useState(false);

  const onIntroDone = () => {
    setIntroDone(true);
    runEntryShowTelemetry();
  };

  return {
    currentEntry,
    entryDisplayIndex,
    entryIndex,
    entryInitialTouched,
    entryInitialValues,
    entryValidationSchema,
    introDone,
    FieldsetComponent,
    getFieldsetComponentByEntryKey,
    initializeWizard,
    isFading,
    isWizardInitialized,
    onIntroDone,
    skipEntry,
    submitEntry,
    totalEntries,
  };
}

export default useProfileEntryCorrectionWizard;
